import i18next from "i18next";
import _ from "lodash";
import { useSelector } from "react-redux";

import { getPassTypeCountString, normalizePrice } from "../../../utils";
import { CashbackBadge } from "../../ui";

import { LineDivider, PriceItem, Wrapper } from "./style";

// заказ не состоялся / отменён / возвращён — обещанный кэшбек не начислится, бейдж не показываем
const CASHBACK_HIDDEN_STATUSES = [
  "bookingFail",
  "payFail",
  "bookingCancel",
  "bookingCancelFromClient",
  "bookingCancelFromAdmin",
  "ticketingFail",
  "refund",
  "void",
];

const OrderPriceComponent = ({ commission }) => {
  const { t } = i18next;
  const {
    orderData: {
      total_price,
      passenger_data,
      currency,
      options,
      purchased_options_total_price,
      cashback,
      cashback_currency,
      cashback_accrued,
      booking_status,
      order_status,
    },
    availablePaymentMethods,
    paymentMethod,
  } = useSelector(state => state.order);
  // способы оплаты с комиссией: OrderComponent их не передаёт — берём из стора, иначе комиссия в цене не показывалась
  const paymentMethods = commission || availablePaymentMethods;

  const renderPassengersAmount = () => {
    const adtCount = _.filter(passenger_data, passenger => passenger.type === "adt").length;
    const chdCount = _.filter(passenger_data, passenger => passenger.type === "chd").length;
    const insCount = _.filter(passenger_data, passenger => passenger.type === "ins").length;
    const infCount = _.filter(passenger_data, passenger => passenger.type === "inf").length;

    let adtAmount = 0;
    let chdAmount = 0;
    let insAmount = 0;
    let infAmount = 0;

    if (adtCount > 0) {
      adtAmount = _.find(passenger_data, passenger => passenger.type === "adt").total_price;
    }

    if (chdCount > 0) {
      chdAmount = _.find(passenger_data, passenger => passenger.type === "chd").total_price;
    }

    if (insCount > 0) {
      insAmount = _.find(passenger_data, passenger => passenger.type === "ins").total_price;
    }

    if (infCount > 0) {
      infAmount = _.find(passenger_data, passenger => passenger.type === "inf").total_price;
    }

    if (_.isNull(adtAmount)) {
      return null;
    }

    return (
      <>
        <PriceItem $isSmallText>
          <span>
            - {adtCount > 1 && adtCount} {getPassTypeCountString(adtCount, "adt")}
          </span>
          <span>
            {normalizePrice(adtAmount)} <span>{currency}</span> {adtCount > 1 && "х" + adtCount}
          </span>
        </PriceItem>

        {chdCount ? (
          <PriceItem $isSmallText>
            <span>
              - {chdCount > 1 && chdCount} {getPassTypeCountString(chdCount, "chd")}
            </span>
            <span>
              {normalizePrice(chdAmount)} <span>{currency}</span> {chdCount > 1 && "х" + chdCount}
            </span>
          </PriceItem>
        ) : null}

        {insCount ? (
          <PriceItem $isSmallText>
            <span>
              - {insCount > 1 && insCount} {getPassTypeCountString(insCount, "ins")} ({t("inf_with_seat_second_label")})
            </span>
            <span>
              {normalizePrice(insAmount)} <span>{currency}</span> {insCount > 1 && "х" + insCount}
            </span>
          </PriceItem>
        ) : null}

        {infCount ? (
          <PriceItem $isSmallText>
            <span>
              - {infCount > 1 && infCount} {getPassTypeCountString(infCount, "inf")}
            </span>
            <span>
              {normalizePrice(infAmount)} <span>{currency}</span> {infCount > 1 && "х" + infCount}
            </span>
          </PriceItem>
        ) : null}
      </>
    );
  };

  // Найти выбранный метод оплаты и получить данные о комиссии
  const selectedPaymentMethod = paymentMethods?.find(method => method.id === paymentMethod);
  const commissionAmount = selectedPaymentMethod?.commission_value || 0;
  const hasCommission = commissionAmount > 0;

  // Дополнительные услуги (eSIM и т.п.): выбранные до оплаты и уже оформленные — сумму считает бек
  // услуга перевозчика (багаж, место): в расшифровке — с рейсом и пассажиром, чтобы было видно, кому и куда
  const optionLabel = option => {
    const name = option.name || option.option_name || "";
    const parts = [name];
    // у места плечо уже может быть в названии («Место 6A · KTM-DXB FZ1134») — не повторяем
    if (option.segment_name && !name.includes(option.segment_name)) parts.push(option.segment_name);
    if (option.passenger_index !== null && option.passenger_index !== undefined) {
      const passenger = (passenger_data || [])[option.passenger_index];
      if (passenger) parts.push([passenger.surname, passenger.name].filter(Boolean).join(" "));
    }
    return parts.join(" · ");
  };

  const purchasedOptions = (options || []).filter(option => ["selected", "confirmed", "failed"].includes(option.status));
  const optionsAmount = _.toFinite(purchased_options_total_price);
  const hasOptions = purchasedOptions.length > 0 && optionsAmount > 0;

  // Рассчитать итоговую сумму: билет + услуги + комиссия
  const totalWithCommission =
    _.toFinite(total_price) + (hasOptions ? optionsAmount : 0) + (hasCommission ? _.toFinite(commissionAmount) : 0);

  return (
    <Wrapper>
      <PriceItem>
        <span>{t("flights_tickets")}</span>
        <span>
          {normalizePrice(_.toFinite(total_price).toFixed(2))} <span>{currency}</span>
        </span>
      </PriceItem>

      {renderPassengersAmount()}

      {hasOptions && (
        <>
          <PriceItem>
            <span>{t("additional_options_total")}</span>
            <span>
              {normalizePrice(optionsAmount.toFixed(2))} <span>{currency}</span>
            </span>
          </PriceItem>

          {purchasedOptions.map(option => (
            <PriceItem key={option.id} $isSmallText>
              <span>
                - {optionLabel(option)} {option.quantity > 1 && "х" + option.quantity}
              </span>
              <span>
                {normalizePrice(_.toFinite(option.total_price).toFixed(2))} <span>{currency}</span>
              </span>
            </PriceItem>
          ))}
        </>
      )}

      {hasCommission && (
        <PriceItem $isSmallText>
          <span className="commission">- {t("merchant_commission")}</span>
          <span className="commission">
            {normalizePrice(_.toFinite(commissionAmount).toFixed(2))} <span>{currency}</span>
          </span>
        </PriceItem>
      )}

      <LineDivider />

      <PriceItem $isTotal>
        <span>{t("total")}</span>
        <span>
          {normalizePrice(totalWithCommission.toFixed(2))} <span>{currency}</span>
        </span>
      </PriceItem>
      {/* кэшбек b2c: уже зачислен (cashback_accrued) или будет начислен после вылета */}
      {cashback_accrued !== null && cashback_accrued !== undefined ? (
        <CashbackBadge amount={cashback_accrued} currency={cashback_currency || currency} accrued />
      ) : (
        !CASHBACK_HIDDEN_STATUSES.includes(booking_status) &&
        order_status !== "orderFail" && <CashbackBadge amount={cashback} currency={cashback_currency || currency} />
      )}
    </Wrapper>
  );
};

export default OrderPriceComponent;
